import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Wrench } from "lucide-react";
import { useLanguage } from "../i18n";
import { SERVICE_IMAGES } from "../data/cars";

const Hero = () => {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 200]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="grain relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={SERVICE_IMAGES.hero}
          alt="Dark muscle car under red garage lights"
          className="h-[115%] w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0A0B0E]/55" />
        <div className="spotlight absolute inset-0" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0A0B0E] to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative mx-auto w-full max-w-7xl px-5 pb-0 pt-36 sm:px-8 lg:pt-44"
      >
        <motion.p
          key={`eyebrow-${lang}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="hero-eyebrow"
          className="font-mono-accent mb-6 text-xs font-medium uppercase tracking-[0.35em] text-[#E63946]"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="font-display max-w-5xl text-4xl font-extrabold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${i === 1 ? "text-[#E63946]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          key={`sub-${lang}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          data-testid="hero-subheading"
          className="mt-6 max-w-xl text-base leading-relaxed text-[#9E9E9E] sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#gallery"
            data-testid="hero-cta-inventory"
            className="group flex items-center gap-2 rounded-full bg-[#E63946] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-[#FF4D5A]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-service"
            className="group flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-[#E63946] hover:text-[#E63946]"
          >
            <Wrench className="h-4 w-4" />
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          data-testid="hero-stats-bar"
          className="mt-16 grid grid-cols-2 border-t border-white/10 lg:grid-cols-4"
        >
          {t.hero.stats.map((s, i) => (
            <div
              key={`${lang}-stat-${i}`}
              className="flex items-center gap-2 border-r border-white/10 px-4 py-5 last:border-r-0 [&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r"
              data-testid={`hero-stat-${i}`}
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#E63946]" />
              <span className="font-mono-accent text-[11px] font-medium uppercase tracking-widest text-[#F8F9FA] sm:text-xs">
                {s}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-6 right-8 hidden text-[#6C757D] lg:block"
      >
        <ArrowDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
};

export default Hero;
